<template>
  <div class="blocking-response-feed">
    <div class="filters">
      <div class="flex custom-gaps column">
        <div class="title flex gaps align-center">
          <div class="box grow">
            <h5>
              Recommendations <span>({{ recommendationCount }})</span>
            </h5>
          </div>
          <i @click="close()" class="box close material-icons cursor-pointer">close</i>
        </div>
        <div class="search flex gaps align-center">
          <div class="box grow" style="position: relative">
            <el-input size="small" placeholder="Search recommendations" v-model="filterInput"></el-input>
            <blocking-response-dropdown
              ref="filterDropdown"
              :selection="filterStatusSelection"
              @update="filter()"
            ></blocking-response-dropdown>
          </div>
          <div class="box">&nbsp;</div>
        </div>
        <div class="action-buttons flex gaps box bottom align-center">
          <div class="box grow">
            <el-tooltip
              class="item"
              effect="dark"
              :content="
                this.selected.length == 0 ? 'You must select some recommendations' : 'Dismiss selected recommendations'
              "
              placement="bottom"
              :enterable="false"
            >
              <el-button
                size="small"
                v-bind:class="{ hidden: selectableRecommendationCount == 0 }"
                @click="confirmBulkMarkIocRecommendation('Dismissed')"
                :disabled="disabled || this.selected.length == 0"
                >Dismiss</el-button
              ></el-tooltip
            >
          </div>
        </div>
        <div class="select-all flex gaps box align-center">
          <div class="box">
            <el-tooltip
              class="item"
              effect="dark"
              :content="selectableRecommendationCount == 0 ? 'There are no selectable recommendations' : 'Select all'"
              placement="bottom"
              :enterable="false"
            >
              <el-checkbox v-model="allSelected" :disabled="selectableRecommendationCount == 0"></el-checkbox
            ></el-tooltip>
          </div>
          <div class="label box grow">{{ selected.length }} of {{ selectableRecommendationCount }} selected</div>
        </div>
      </div>
    </div>
    <el-collapse v-model="activeNames" class="recommendations" @change="handleAccordionChange">
      <el-collapse-item
        ref="feedItems"
        v-for="host in sorted"
        v-show="host.recommendations.length > 0"
        v-bind:key="host.id"
        :title="host.hostname"
        :name="host.hostname"
      >
        <template slot="title">
          <i v-if="host.agentInfo.system && host.agentInfo.system.os === 'linux'" class="fab fa-linux"></i>
          <i v-if="host.agentInfo.system && host.agentInfo.system.os === 'windows'" class="fab fa-windows"></i>
          {{ host.hostname }}
        </template>
        <div v-for="recommendation in host.recommendations" :key="`${recommendation.iocHash}${recommendation.index}`">
          <blocking-response-feed-item
            :active="active"
            :selected="selected"
            :disabled="disabled"
            :recommendation="recommendation"
            :mitigation-progress-state="mitigationProgressState"
            @select="select"
            @execute="executeResponseAction(recommendation)"
            @dismiss="markIocRecommendation(recommendation, 'Dismissed')"
            @done="markIocRecommendation(recommendation, 'Done')"
            @reset="markIocRecommendation(recommendation, 'Undo')"
            @activate="activate(recommendation)"
            @close="activate({ iocHashIndex: '' })"
          />
        </div>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { JIRA_TICKET_STATUS_CHANGE } from '@/store/modules/jira/jira-setting.type'
import {
  TRAIL_RESPONSE_ACTION_REQ,
  TRAIL_RESPONSE_IOC_ACTION_REQ,
  BULK_TRAIL_RESPONSE_IOC_ACTION_REQ
} from '@/store/modules/trails/trailsinfo.type'
import BlockingResponseFeedItem from './BlockingResponseFeedItem'
import BlockingResponseDropdown from '../dropdown/BlockingResponseDropdown'

export default {
  name: 'BlockingResponseFeed',
  components: { BlockingResponseFeedItem, BlockingResponseDropdown },
  props: {
    mitigationProgressState: {
      type: String,
      default () {
        return 'mitigation-not-started'
      }
    },
    recommendations: {
      type: Array,
      default () {
        return []
      }
    }
  },
  data () {
    return {
      active: '',
      activeNames: [],
      sorted: [],
      selected: [],
      filterInput: '',
      filterStatusSelection: ['default', 'pending', 'executed', 'failed'],
      recommendationCount: 0,
      selectableRecommendationCount: 0,
      userInitiatedCollapse: false
    }
  },
  created () {
    this.filter()
  },
  updated () {
    this.computeSelectableRecommendationCount('Dismissed')
  },
  methods: {
    close () {
      this.$emit('close')
    },
    handleAccordionChange () {
      this.userInitiatedCollapse = true
    },
    select (recommendation) {
      let index = this.selected.indexOf(recommendation.iocHashIndex)
      if (index >= 0) {
        this.selected.splice(index, 1)
      } else {
        this.selected.push(recommendation.iocHashIndex)
      }
      this.computeSelectableRecommendationCount('Dismissed')
    },
    activate (recommendation) {
      this.active = recommendation.iocHashIndex
      this.$emit('selection-change', recommendation)
    },
    computeSelectableRecommendationCount (state) {
      let count = 0
      this.$refs.feedItems &&
        this.$refs.feedItems.forEach(host => {
          host.$children.forEach(recommendation => {
            let data = recommendation.recommendation
            if (state.toLowerCase() === 'dismissed') {
              if (recommendation.showDismiss()) {
                count++
              }
            }
          })
        })
      this.selectableRecommendationCount = count
    },
    executeResponseAction (recommendation) {
      let email = this.userProfile.email
      this.$store.dispatch(TRAIL_RESPONSE_ACTION_REQ, {
        trail_id: recommendation.globalTrailIdHash,
        ioc_hash: recommendation.iocHash,
        comment: recommendation.description,
        assigned_from: email,
        owner_id: email,
        actions: [
          {
            index: recommendation.index,
            action: recommendation.latestAction.action
          }
        ]
      }).then(() => {
        const message = `${recommendation.latestAction.actionType} : ${recommendation.description}`
        this.jiraActionHandler(recommendation.globalTrailIdHash, recommendation.latestAction.actionType, message)
      })
    },
    markIocRecommendation (recommendation, state) {
      let email = this.userProfile.email
      this.$store
        .dispatch(TRAIL_RESPONSE_ACTION_REQ, {
          ioc_hash: recommendation.iocHash,
          trail_id: recommendation.globalTrailIdHash,
          assigned_from: email,
          owner_id: email,
          index: recommendation.index,
          state: state,
          comment: recommendation.description,
          actions: [
            {
              index: recommendation.index,
              action: state.toUpperCase()
            }
          ]
        })
        .then(() => {
          let index = this.selected.indexOf(recommendation.iocHashIndex)
          if (index >= 0) {
            this.selected.splice(index, 1)
          }
          const message = `${state} : ${recommendation.description}`
          this.jiraActionHandler(recommendation.globalTrailIdHash, state, message)
        })
    },
    bulkExecuteResponseAction () {
      this.$refs.feedItems.forEach(host => {
        host.$children.forEach(recommendation => {
          let data = recommendation.recommendation
          if (this.selected.includes(data.iocHashIndex)) {
            if (recommendation.showExecute()) {
              this.executeResponseAction(data)
            }
          }
        })
      })
      this.selected = []
    },
    bulkMarkIocRecommendation (state, force = false) {
      let email = this.userProfile.email
      let recommendations = []
      this.$refs.feedItems.forEach(host => {
        host.$children.forEach(recommendation => {
          let data = recommendation.recommendation
          if (force || this.selected.includes(data.iocHashIndex)) {
            if (state.toLowerCase() === 'dismissed') {
              if (recommendation.showDismiss()) {
                recommendations.push({
                  ioc_hash: data.iocHash,
                  trail_id: data.globalTrailIdHash,
                  assigned_from: email,
                  owner_id: email,
                  index: data.index,
                  state: state,
                  comment: data.description,
                  actions: [
                    {
                      index: data.index,
                      action: state.toUpperCase()
                    }
                  ]
                })
              }
            } else if (state.toLowerCase() === 'done') {
              if (recommendation.showDone()) {
                recommendations.push({
                  ioc_hash: data.iocHash,
                  trail_id: data.globalTrailIdHash,
                  assigned_from: email,
                  owner_id: email,
                  index: data.index,
                  state: state,
                  comment: data.description,
                  actions: [
                    {
                      index: data.index,
                      action: state.toUpperCase()
                    }
                  ]
                })
              }
            }
          }
        })
      })

      this.$store.dispatch(BULK_TRAIL_RESPONSE_IOC_ACTION_REQ, recommendations).then(() => {
        for (let rec of recommendations) {
          const messageForJira = `${rec.state}: ${rec.comment}`
          this.jiraActionHandler(rec.trail_id, rec.state, messageForJira)
        }
        this.selected = []
      })
    },
    confirmBulkMarkIocRecommendation (state) {
      let h = this.$createElement
      let recommendations = []
      this.$refs.feedItems.forEach(host => {
        host.$children.forEach(recommendation => {
          let data = recommendation.recommendation
          if (this.selected.includes(data.iocHashIndex)) {
            if (state.toLowerCase() === 'Dismissed') {
              if (recommendation.showDismiss()) {
                recommendations.push(h('li', null, data.description))
              }
            }
          }
        })
      })
      this.$msgbox({
        title: 'Confirm Mark as Dismissed',
        message: h('ul', { style: 'max-height: 300px; overflow: scroll' }, recommendations),
        showCancelButton: true,
        confirmButtonText: 'Dismiss',
        cancelButtonText: 'Cancel',
        beforeClose: (action, instance, done) => {
          if (action === 'confirm') {
            done()
            this.bulkMarkIocRecommendation(state)
          } else {
            done()
          }
        }
      })
    },
    filter () {
      this.recommendationCount = 0
      this.sorted = this.sort()
      this.sorted.forEach(host => {
        host.agentInfo = _.find(this.agentList.models, { agentId: host.id }) || {}
        host.hostname = _.isUndefined(host.agentInfo) ? host.id : host.agentInfo.customHostname

        // filter recommendations
        host.recommendations = host.recommendations.filter(recommendation => {
          if (this.$refs.filterDropdown.$data.automated) {
            if (!recommendation.action) {
              return false
            }
          }
          if (this.filterInput) {
            let compared = recommendation.description.toLowerCase() + ' ' + host.hostname
            let filter = this.filterInput.toLowerCase()
            if (compared.indexOf(filter) < 0) {
              return false
            }
          }
          let results = this.filterStatusSelection.map(key => {
            return recommendation.isStatus(key)
          })
          return results.indexOf(true) >= 0
        })

        // sort recommendations by priority
        host.recommendations = host.recommendations.sort((a, b) => {
          let getPriorityValue = r => {
            let priority
            let hash = r.iocHash.split('-')
            switch (r.priority) {
              case 'high':
                priority = 3
                break
              case 'medium':
                priority = 2
                break
              case 'low':
                priority = 1
                break
              default:
                priority = 0
            }
            return priority + '-' + hash[2] + '-' + r.index
          }
          if (getPriorityValue(a) > getPriorityValue(b)) {
            return -1
          } else {
            return 1
          }
        })

        // make each host collapse active
        if (!this.userInitiatedCollapse) {
          if (!this.activeNames.includes(host.hostname) && host.recommendations.length > 0) {
            this.activeNames.push(host.hostname)
          }
        }

        // update recommendation count
        this.recommendationCount += host.recommendations.length
      })
    },
    sort () {
      let copyOfRecommendations = _.cloneDeep(this.recommendations)
      copyOfRecommendations.sort((a, b) => {
        if (a.id > b.id) {
          return -1
        } else {
          return 1
        }
      })
      return _.filter(copyOfRecommendations, rec => {
        return rec.recommendations && rec.recommendations.length > 0
      })
    },
    jiraActionHandler (trailId, statusType, commentForTicket) {
      let payload = {
        trail_id: trailId,
        status_type: statusType,
        comment_for_ticket: commentForTicket
      }
      this.$store.dispatch(JIRA_TICKET_STATUS_CHANGE, payload);
    }
  },
  watch: {
    filterInput () {
      let self = this
      this.timeout && clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        self.filter()
      }, 350)
    },
    recommendations () {
      this.filter()
      this.computeSelectableRecommendationCount('Dismissed')
    }
  },
  computed: {
    ...mapGetters(['agentList', 'userProfile', 'trailResponseActionStatus']),
    disabled: {
      get () {
        return this.trailResponseActionStatus === 'inprogress'
      }
    },
    allSelected: {
      get () {
        if (this.selectableRecommendationCount !== 0) {
          if (this.selected.length === this.selectableRecommendationCount) {
            return true
          } else {
            return false
          }
        } else {
          return false
        }
      },
      set (value) {
        if (this.selected.length === this.selectableRecommendationCount) {
          this.selected = []
        } else {
          let selected = []
          this.$refs.feedItems.forEach(host => {
            host.$children.forEach(recommendation => {
              let data = recommendation.recommendation
              if (recommendation.showDismiss()) {
                selected.push(recommendation.recommendation.iocHashIndex)
              }
            })
          })
          this.selected = selected
        }
        this.computeSelectableRecommendationCount('Dismissed')
      }
    }
  }
}
</script>

<style scoped>
@import 'assets/styles/_variables.css';

.blocking-response-feed {
  height: 100%;

  .filters {
    .title {
      padding: 7px 5px 0 15px;

      .close {
        color: #cccccc;
        font-size: 24px;
      }
    }

    .search {
      padding: 0 15px 0 15px;

      >>> .el-input__inner {
        padding: 0 15px 0 70px;

        &:focus {
          border: 1px solid #dcdfe6;
        }
      }
    }

    .action-buttons {
      padding: 0 15px 0 15px;

      .hidden {
        visibility: hidden;
      }
    }

    .select-all {
      padding: 10px 15px 10px 15px;
      border-top: solid 1px var(--border-color);

      .label {
        color: #666666;
        font-size: 12px;
      }
    }

    .custom-gaps:not(.reverse) > :not(:last-child) {
      margin-bottom: 10px;
    }
  }

  >>> .el-collapse {
    position: relative;
    height: calc(100% - 197px);
    overflow: scroll;
    border-color: var(--border-color);
  }

  >>> .el-checkbox__inner {
    width: 15px;
    height: 15px;
    border-radius: 0;
    border-color: var(--border-color);
  }

  >>> .el-collapse-item__header {
    padding: 0 0 0 16px;
    height: 40px;
    color: #333333;
    font-size: 13px;
    font-weight: bold;
    line-height: 40px;
    background: #f2f4f4;

    .fab {
      margin: 0 15px 0 0;
      color: #666666;
      font-size: 16px;
    }
  }

  >>> .el-collapse-item__content {
    font-size: 14px;
    padding-bottom: 0;
    border-top: solid 1px #e3e5e7;
  }

  >>> .el-collapse-item__content > div:not(:first-child) {
    border-top: solid 1px #f0f0f0;
  }

  .no-recommendations {
    min-height: 150px;
    color: #666666;
    font-size: 12px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/feed/BlockingResponseFeed.vue